import { type Component, createContext, createMemo, createSignal, For, type JSX, onCleanup, onMount, Show, useContext } from 'solid-js'
import { twMerge } from 'tailwind-merge'
import type { ToastContextValueType, ToastItemType, ToastPositionType, ToastType } from '~/types/useModal'
import { getGsap, MOTION, prefersReducedMotion } from '~/utils/gsap'
import Button from './Button'
import Icon from './Icon'

const ToastContext = createContext<ToastContextValueType>()

export const ToastProvider: Component<{ children: JSX.Element; position?: ToastPositionType }> = props => {
  const [toasts, setToasts] = createSignal<ToastItemType[]>([])
  const [pos, setPos] = createSignal<ToastPositionType>(props.position ?? 'top-right')

  const addToast = (toast: Omit<ToastItemType, 'id'>): string => {
    const id = Math.random().toString(36).substring(2, 9)
    const newToast: ToastItemType = {
      id,
      duration: 4000,
      dismissible: true,
      ...toast
    }

    setToasts(prev => {
      if (prev.length >= 5) {
        return [...prev.slice(1), newToast]
      }
      return [...prev, newToast]
    })

    return id
  }

  const removeToast = (id: string) => {
    setToasts(prev => prev.filter(toast => toast.id !== id))
  }

  const clearToasts = () => {
    setToasts([])
  }

  const value: ToastContextValueType = {
    toasts,
    addToast,
    removeToast,
    clearToasts,
    position: () => pos(),
    setPosition: (p: ToastPositionType) => setPos(p)
  } as Required<ToastContextValueType>

  return (
    <ToastContext.Provider value={value}>
      {props.children}
      <ToastContainer />
    </ToastContext.Provider>
  )
}

export const useToast = () => {
  const context = useContext(ToastContext)
  if (!context) {
    throw new Error('useToast must be used within a ToastProvider')
  }
  return context
}

const ToastContainer: Component = () => {
  const context = useToast()
  const { toasts, removeToast } = context

  const currentPosition = createMemo(() => context.position?.())

  const containerClass = createMemo(() => {
    switch (currentPosition()) {
      case 'top-left':
        return 'fixed top-4 left-4 z-[1100] space-y-3 pointer-events-none items-start'
      case 'top-right':
        return 'fixed top-4 right-4 z-[1100] space-y-3 pointer-events-none items-end'
      case 'bottom-left':
        return 'fixed bottom-4 left-4 z-[1100] space-y-3 pointer-events-none items-start'
      case 'bottom-right':
        return 'fixed bottom-4 right-4 z-[1100] space-y-3 pointer-events-none items-end'
      case 'top-center':
        return 'fixed top-4 left-1/2 transform -translate-x-1/2 z-[1100] space-y-3 pointer-events-none items-center'
      case 'bottom-center':
        return 'fixed bottom-4 left-1/2 transform -translate-x-1/2 z-[1100] space-y-3 pointer-events-none items-center'
      default:
        return 'fixed top-4 right-4 z-[1100] space-y-3 pointer-events-none items-end'
    }
  })

  return (
    <div class={containerClass()}>
      <For each={toasts()}>{(toast, index) => <ToastItem toast={toast} onRemove={() => removeToast(toast.id)} index={index()} />}</For>
    </div>
  )
}

const ACCENT: Record<ToastType, { border: string; bg: string; icon: string; text: string; ring: string; bar: string }> = {
  success: {
    border: 'border-l-green-500',
    bg: 'bg-green-500/10 dark:bg-green-500/15',
    icon: 'material-symbols-light:check-circle-outline',
    text: 'text-green-600 dark:text-green-400',
    ring: 'ring-green-500/20',
    bar: 'bg-green-500'
  },
  danger: {
    border: 'border-l-red-500',
    bg: 'bg-red-500/10 dark:bg-red-500/15',
    icon: 'material-symbols-light:cancel-outline',
    text: 'text-red-600 dark:text-red-400',
    ring: 'ring-red-500/20',
    bar: 'bg-red-500'
  },
  warning: {
    border: 'border-l-amber-500',
    bg: 'bg-amber-500/10 dark:bg-amber-500/15',
    icon: 'material-symbols-light:warning-outline',
    text: 'text-amber-600 dark:text-amber-400',
    ring: 'ring-amber-500/20',
    bar: 'bg-amber-500'
  },
  info: {
    border: 'border-l-blue-500',
    bg: 'bg-blue-500/10 dark:bg-blue-500/15',
    icon: 'material-symbols-light:info-outline',
    text: 'text-blue-600 dark:text-blue-400',
    ring: 'ring-blue-500/20',
    bar: 'bg-blue-500'
  },
  default: {
    border: 'border-l-slate-400',
    bg: 'bg-slate-500/10 dark:bg-slate-500/15',
    icon: 'material-symbols-light:notifications-outline',
    text: 'text-slate-600 dark:text-slate-400',
    ring: 'ring-slate-500/20',
    bar: 'bg-slate-400'
  }
}

const ToastItem: Component<{
  toast: ToastItemType
  onRemove: () => void
  index: number
}> = props => {
  const context = useToast()
  const position = createMemo(() => context.position?.())

  let itemRef: HTMLDivElement | undefined
  let barRef: HTMLDivElement | undefined
  let autoTimer: ReturnType<typeof setTimeout> | undefined
  let removed = false

  const accent = createMemo(() => ACCENT[props.toast.type] ?? ACCENT.default)

  onMount(() => {
    const g = getGsap()
    if (g && !prefersReducedMotion() && itemRef) {
      g.fromTo(itemRef, { autoAlpha: 0, y: -10, scale: 0.96 }, { autoAlpha: 1, y: 0, scale: 1, duration: MOTION.enter, ease: MOTION.easeOut, delay: props.index * 0.05 })
    } else if (itemRef) {
      itemRef.style.opacity = '1'
      itemRef.style.visibility = 'visible'
    }

    // Barra de progreso
    const dur = props.toast.duration ?? 4000
    if (dur > 0 && barRef) {
      if (g && !prefersReducedMotion()) {
        g.fromTo(barRef, { scaleX: 1 }, { scaleX: 0, duration: dur / 1000, ease: 'none' })
      } else {
        barRef.style.transform = 'scaleX(0)'
        barRef.style.transition = `transform ${dur}ms linear`
      }
    }

    if (dur > 0) autoTimer = setTimeout(handleRemove, dur)
  })

  onCleanup(() => clearTimeout(autoTimer))

  const handleRemove = () => {
    if (removed) return
    removed = true
    clearTimeout(autoTimer)
    const g = getGsap()
    if (!g || prefersReducedMotion() || !itemRef) {
      props.onRemove()
      return
    }
    g.to(itemRef, { autoAlpha: 0, y: -6, scale: 0.97, duration: MOTION.exit, ease: 'power2.in', onComplete: () => props.onRemove() })
  }

  return (
    <div
      ref={itemRef}
      class={twMerge(
        'relative flex items-start gap-3 w-80 max-w-[calc(100vw-2rem)] pl-4 pr-3 py-3 rounded-xl border border-l-4',
        'bg-white dark:bg-slate-900 border-slate-200 dark:border-slate-800',
        'shadow-[0_4px_24px_rgba(0,0,0,0.08)] dark:shadow-[0_4px_24px_rgba(0,0,0,0.4)]',
        'pointer-events-auto',
        accent().border
      )}
      style={{
        opacity: 0,
        'transform-origin': position()?.includes('right') ? 'top right' : position()?.includes('left') ? 'top left' : 'top center'
      }}
    >
      {/* Icono */}
      <div class="shrink-0 pt-0.5">
        <Icon icon={accent().icon} size={24} class={accent().text} />
      </div>

      {/* Texto */}
      <div class="flex-1 min-w-0 pt-0.5">
        <p class="font-semibold text-sm leading-snug text-slate-900 dark:text-slate-100">{props.toast.title}</p>
        <Show when={props.toast.description}>
          <p class="text-[13px] mt-0.5 leading-relaxed text-slate-500 dark:text-slate-400">{props.toast.description}</p>
        </Show>

        <Show when={props.toast.action}>
          <Button
            onClick={() => {
              props.toast.action?.onClick()
              handleRemove()
            }}
            variant="text"
            size="sm"
            class="mt-1.5 px-0 py-0 h-auto text-xs font-semibold"
          >
            {props.toast.action?.label}
          </Button>
        </Show>
      </div>

      {/* Cerrar */}
      <Show when={props.toast.dismissible}>
        <button
          type="button"
          onClick={handleRemove}
          aria-label="Cerrar notificación"
          class="shrink-0 h-6 w-6 rounded-md mt-0.5 text-slate-400 hover:text-slate-600 hover:bg-slate-100 dark:hover:text-slate-300 dark:hover:bg-slate-800 transition-colors duration-150 inline-flex items-center justify-center"
        >
          <Icon icon="material-symbols-light:close" size={15} />
        </button>
      </Show>

      {/* Barra de progreso */}
      <div class="absolute bottom-0 left-4 right-3 h-0.5 rounded-full bg-slate-100 dark:bg-slate-800 overflow-hidden">
        <div ref={barRef} class={twMerge('h-full rounded-full origin-left', accent().bar)} />
      </div>
    </div>
  )
}

export const toast = {
  success: (title: string, description?: string, options?: Partial<Omit<ToastItemType, 'id' | 'type' | 'title' | 'description'>>) => {
    const { addToast } = useToast()
    return addToast({ type: 'success', title, description, ...options })
  },
  error: (title: string, description?: string, options?: Partial<Omit<ToastItemType, 'id' | 'type' | 'title' | 'description'>>) => {
    const { addToast } = useToast()
    return addToast({ type: 'danger' as ToastType, title, description, ...options })
  },
  warning: (title: string, description?: string, options?: Partial<Omit<ToastItemType, 'id' | 'type' | 'title' | 'description'>>) => {
    const { addToast } = useToast()
    return addToast({ type: 'warning', title, description, ...options })
  },
  info: (title: string, description?: string, options?: Partial<Omit<ToastItemType, 'id' | 'type' | 'title' | 'description'>>) => {
    const { addToast } = useToast()
    return addToast({ type: 'info', title, description, ...options })
  },
  default: (title: string, description?: string, options?: Partial<Omit<ToastItemType, 'id' | 'type' | 'title' | 'description'>>) => {
    const { addToast } = useToast()
    return addToast({ type: 'default', title, description, ...options })
  }
}

export type { ToastContextValueType, ToastItemType, ToastPositionType, ToastType }

export default useToast
